import { useCallback } from 'react';

import { CREDITS_BALANCE_POLL_INTERVAL_MS } from './useCreditsBalance';
import { useRefetchOnWindowFocus } from './useRefetchOnWindowFocus';
import {
  TeamCreditBalance,
  useGetTeamCredits,
} from '../../services/credits.service';

export interface UseTeamCreditsBalanceResult {
  balance: TeamCreditBalance | undefined;
  loading: boolean;
  refetch: () => void;
}

/**
 * enabled gates both the query (skip) and the refresh side-effects. Callers
 * pass a teamId only when there's exactly one unambiguous team in context —
 * see Flows.tsx's effectiveTeamId (the "All teams" filter has no single team
 * to ask teamCredits about).
 */
export function useTeamCreditsBalance(
  enabled: boolean,
  teamId: string | undefined,
): UseTeamCreditsBalanceResult {
  const { data, loading, refetch } = useGetTeamCredits(teamId, {
    skip: !enabled,
    pollInterval: enabled ? CREDITS_BALANCE_POLL_INTERVAL_MS : undefined,
  });

  const refetchBalance = useCallback(() => {
    refetch();
  }, [refetch]);

  useRefetchOnWindowFocus(refetchBalance, enabled && !!teamId);

  return {
    balance: data?.teamCredits ?? undefined,
    loading: enabled && !!teamId && loading,
    refetch: refetchBalance,
  };
}
